// The Meeting Studio's one page. /m/<code> is a person's meeting link; /new creates a meeting (pilot); / lets someone
// type their code. A meeting with a project canvas is a guided tour — the sales manager's navigator and the
// customer's canvas; one without keeps the plain video call.
import { createLocalTracks } from "livekit-client";
import { getLink, resolveCode, joinPass } from "./api.js";
import { restoreBackground } from "./bgpicker.js";
import { AUDIO_CAPTURE, CAMERA, RECONNECT_FOR_MS, reopenPlan } from "./rtc.js";
import { showAdmin } from "./admin.js";
import { runCall } from "./call.js";
import { runCustomer } from "./customer.js";
import { runNavigator } from "./navigator.js";
import { openSession } from "./session.js";
import { showSummary } from "./summary.js";
import { loadCanvas } from "./canvas/render.js";
import { LANGS } from "./canvas/i18n.js";
import { h } from "./dom.js";
import { showEnd, showProblem, showRejoining } from "./end.js";
import { customerLobby, lobby } from "./lobby.js";
import { runSite, siteLobby } from "./site.js";
import { isCanvas, product, words, wordmark } from "./product.js";
import { roomPage } from "./room.js";

const root = document.getElementById("app");

const stopAll = (joined) => { for (const t of [joined?.audioTrack, joined?.videoTrack]) { try { t?.stop(); } catch { /* already stopped */ } } };

// The devices a person joined with, remembered before the call takes the tracks (it stops them when the room drops),
// so an automatic rejoin opens the same camera and microphone.
async function rememberDevices(joined) {
  if (joined.devices) return;
  joined.devices = { audio: "", video: "" };
  try { joined.devices.audio = (await joined.audioTrack?.getDeviceId?.()) || ""; } catch { /* the default device, then */ }
  try { joined.devices.video = (await joined.videoTrack?.getDeviceId?.()) || ""; } catch { /* the default device, then */ }
}

// The same devices again, with the microphone and camera as the person last had them in the call. A device that
// cannot be opened now leaves that one off — the person is back in the meeting all the same.
async function reopenDevices(joined, last) {
  const plan = reopenPlan(joined, last);
  const out = { ...joined, ...plan.state, devices: plan.devices, audioTrack: null, videoTrack: null, auto: true };
  if (plan.audio) {
    try { [out.audioTrack] = await createLocalTracks({ audio: { ...AUDIO_CAPTURE, ...(plan.devices.audio ? { deviceId: plan.devices.audio } : {}) }, video: false }); }
    catch { out.micOn = false; }
  }
  if (plan.video) {
    try {
      [out.videoTrack] = await createLocalTracks({ audio: false, video: { ...CAMERA, ...(plan.devices.video ? { deviceId: plan.devices.video } : { facingMode: "user" }) } });
      if (!isCanvas) await restoreBackground(out.videoTrack); // before it is published: never the room behind you (N1)
    } catch { out.camOn = false; }
  }
  return out;
}

// A lost connection is rejoined by itself (Ravi, 28 Sep: "during a weak connection the meeting should rejoin"). The
// room already tried for RECONNECT_FOR_MS (rtc.js) before giving up; now the page waits for the network, opens the same
// devices again and goes back in with a fresh pass under the same identity — for as long again. Answers { joined } for
// the next turn, or { reason }: the person chose to leave, the meeting is over or this link is refused, or the time
// ran out.
async function rejoin(root, code, info, joined, last, { failed = false } = {}) {
  // One clock across every automatic attempt for this drop (re-audit 28 Sep: a room that would not connect looped
  // without end — join, fail, join — each rejoin starting its own five minutes). The clock is reset by a call that
  // connected (the loop clears rejoinFrom on a normal return).
  const from = joined.rejoinFrom || Date.now();
  let leaving = false;
  let wake = () => {};
  showRejoining(root, info, () => { leaving = true; wake(); });
  // A wait that ends early when the browser says the network is back, or the person presses Leave.
  const pause = (ms) => new Promise((resolve) => {
    const t = setTimeout(done, ms);
    function done() { clearTimeout(t); window.removeEventListener("online", done); wake = () => {}; resolve(); }
    wake = done;
    window.addEventListener("online", done, { once: true });
  });
  if (failed && !leaving) await pause(3000); // the last attempt got in and fell: a breath before the next, never a spin
  while (!leaving && Date.now() - from < RECONNECT_FOR_MS) {
    if (navigator.onLine === false) { await pause(3000); continue; }
    try {
      const fresh = await getLink(code);
      if (leaving) break;
      if ((fresh.window?.state || "open") !== "open") return { reason: "ended" }; // over, ended or cancelled while away: the end page says so
      const again = await reopenDevices(joined, last);
      if (leaving) { stopAll(again); break; }
      again.rejoinFrom = from;
      return { joined: again };
    } catch (err) {
      if (err.status >= 400 && err.status < 500) return { reason: "lost" }; // the studio's own no: gone, removed, expired
      await pause(3000);
    }
  }
  return { reason: leaving ? "left" : "lost" };
}

// `pre`: the devices a guest already chose on a ClikMeet room page, admitted from its door — the first turn goes straight
// into the call with them, no second join page (CR-20260926-001 U4).
async function meeting(code, pre = null) {
  let info;
  try {
    info = await getLink(code);
  } catch (err) {
    // A guest admitted from a room's door arrives with their camera and microphone on: let them go (re-audit N2).
    for (const t of [pre?.audioTrack, pre?.videoTrack]) { try { t?.stop(); } catch { /* already stopped */ } }
    showProblem(root, err.status === 404 ? "This link has expired." : "We could not open your meeting.",
      err.status === 404 ? words.expired
        : "Check your connection and open the link again.");
    return;
  }
  const isHost = info.you.role === "host";
  const isSite = info.you.role === "site"; // the site camera (stage 11): its own join page, its own screen
  const withCanvas = Boolean(info.meeting.canvas);
  let notice = "";
  let auto = null; // the devices reopened for an automatic rejoin: the next turn goes straight in, no join page
  const endScreen = (reason) => new Promise((resolve) => showEnd(root, info, reason, () => resolve(true)));
  for (let turn = 0; ; turn++) {
    // Each time round, the meeting is asked again: it may have been ended, or this person removed, since the page
    // opened — and a refusal from Join is said on the fresh join page, not painted over by it.
    if (turn) {
      try { info = { ...(await getLink(code)), you: { ...info.you } }; } catch (err) {
        if (err.status === 404) { showProblem(root, "This link no longer opens the meeting.", notice || words.gone); return; }
      }
    }
    info.notice = notice;
    notice = "";
    // The sales manager's meeting is over: what he comes back for is what the canvas saw, not a join page.
    if (isHost && withCanvas && ["ended", "over"].includes(info.window?.state)) {
      const canvas = await loadCanvas(info.meeting.canvas).catch(() => null);
      await new Promise(() => showSummary(root, { code, info, canvas, reason: "ended", live: {}, rejoin: null }));
      return;
    }
    const joined = auto || (pre && turn === 0 ? pre : isSite ? await siteLobby(root, info)
      : withCanvas && !isHost
        ? await customerLobby(root, info, { hero: `/brand/${info.meeting.canvas}.jpg`, langs: LANGS })
        : await lobby(root, info));
    auto = null;
    await rememberDevices(joined);
    let pass;
    try {
      pass = await joinPass(code, joined.name);
    } catch (err) {
      // The next join page opens its own camera and microphone: let go of these first, or two captures stay live.
      stopAll(joined);
      // A rejoin whose pass did not come because the network went again keeps waiting; the studio's own refusal ends it.
      if (joined.auto && !(err.status >= 400 && err.status < 500)) {
        const r = await rejoin(root, code, info, joined, null, { failed: true });
        if (r.joined) { auto = r.joined; continue; }
        if (await endScreen(r.reason)) continue;
        return;
      }
      notice = err.message;
      continue;
    }
    info.you.name = joined.name;
    let reason, last = null;
    try {
      if (isSite) {
        // The site camera needs no canvas: it shows its own picture, and the glass's marks come in the frame's units.
        const session = await openSession({ pass, joined });
        runSite(root, { info, session, joined });
        reason = await session.ended;
      } else if (withCanvas) {
        const canvas = await loadCanvas(info.meeting.canvas); // the pass above set the cookie that opens it
        const session = await openSession({ pass, joined });
        const nav = isHost ? runNavigator(root, { code, info, pass, canvas, session }) : null;
        if (!isHost) runCustomer(root, { code, info, canvas, session, lang: joined.lang || "en" });
        reason = await session.ended;
        if (isHost) {
          const again = await new Promise((resolve) => showSummary(root, { code, info, canvas, reason, live: nav.dwell(), rejoin: () => resolve(true) }));
          if (again) continue;
        }
      } else {
        const call = await runCall(root, { code, info, pass, joined });
        reason = await call.ended;
        last = call.last();
      }
    } catch (err) {
      console.error(err);
      // The lobby's camera and microphone are ours until the call takes them: a failed start must not leave the
      // camera light on (audit F5).
      stopAll(joined);
      document.body.classList.remove("ms-dark");
      // An automatic rejoin whose room would not connect (the network back for HTTPS, not yet for the call) keeps
      // trying — its own clock bounds it (re-audit 28 Sep: it used to end on a page with no way back).
      if (joined.auto) {
        const r = await rejoin(root, code, info, joined, null, { failed: true });
        if (r.joined) { auto = r.joined; continue; }
        if (await endScreen(r.reason)) continue;
        return;
      }
      showProblem(root, "We could not connect you.", "The meeting could not start on this network. Try again, or switch between Wi-Fi and mobile data.");
      return;
    }
    joined.rejoinFrom = 0; // the room connected: a later drop gets a fresh clock
    // A connection that was lost is rejoined by itself — the plain call and the customer's canvas; the site camera and
    // the navigator (whose summary screen offers Rejoin) are not.
    if (reason === "lost" && !isSite && !(withCanvas && isHost)) {
      const r = await rejoin(root, code, info, joined, last);
      if (r.joined) { auto = r.joined; continue; }
      reason = r.reason;
    }
    const again = await endScreen(reason);
    if (!again) return;
  }
}

// ClikMeet's home page (D125 A): a meeting is opened from ClikWork or from the link someone sent. There is no code to
// type: the code-and-last-four door is Canvas's (a customer's WhatsApp confirmation), and ClikMeet's server has none.
function meetLanding() {
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, wordmark()),
    h("main", { class: "page-main" }, h("div", { class: "end" },
      h("p", { class: "eyebrow", text: product.name }),
      h("h1", { class: "statement", text: "Open your meeting from its link" }),
      h("p", { class: "lede", text: `A ${product.name} meeting opens from the link you were sent, or from Workin. No app to install.` })))));
  document.title = product.title;
}

// The home page: for someone whose link broke, the code from their WhatsApp confirmation.
function landing() {
  if (!isCanvas) return meetLanding();
  const input = h("input", { "aria-label": "Your meeting code", placeholder: "SOL-4K7-2PQ", maxlength: "14", autocomplete: "off", autocapitalize: "characters", spellcheck: "false" });
  const last4 = h("input", { "aria-label": "Last four digits of your phone", placeholder: "Last 4 digits of your phone", maxlength: "4", inputmode: "numeric", autocomplete: "off" });
  const say = h("p", { class: "notice", role: "alert", hidden: true });
  const go = h("button", { class: "btn", type: "submit", text: "Join" });
  const form = h("form", { class: "code-entry" }, input, last4, go);
  const tell = (text, el) => { say.textContent = text; say.hidden = false; (el || input).focus(); };
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const code = input.value.replace(/[^A-Za-z0-9]/g, "");
    const digits = last4.value.replace(/\D/g, "");
    if (!code) return tell("Type the code from your WhatsApp confirmation — it looks like SOL-4K7-2PQ.");
    if (code.length < 9) return tell("That code is too short — it looks like SOL-4K7-2PQ, from your WhatsApp confirmation.");
    if (digits.length !== 4) return tell("Add the last four digits of the phone number you gave us.", last4);
    go.disabled = true;
    try {
      const found = await resolveCode(code, digits);
      location.href = `/m/${found.code}`;
    } catch (err) {
      go.disabled = false;
      tell(err.message || "We could not check that code. Try again.");
    }
  });
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, h("span", { class: "brand canvas-brand" }, h("b", { text: "Walkin" }), h("span", { class: "by", text: " by Clikin.ai" }))),
    h("main", { class: "page-main" }, h("div", { class: "end" },
      h("p", { class: "eyebrow", text: "Walkin by Clikin.ai" }),
      h("h1", { class: "statement", text: "Join your Walkin meeting" }),
      h("p", { class: "lede", text: "The link in your WhatsApp message opens your meeting. If it will not open, type the code from that message here, with the last four digits of your phone. No app, no sign-in." }),
      form, say))));
}

const m = location.pathname.match(/^\/m\/([A-Za-z0-9-]{6,20})\/?$/);
// ClikMeet's room link (M3): the one a calendar invite carries. Canvas has no rooms, so there it is the landing page.
const r = !isCanvas && location.pathname.match(/^\/r\/([A-Za-z0-9]{6,20})\/?$/);
if (m) meeting(m[1]);
else if (r) roomPage(root, r[1], { enter: (code, joined) => meeting(code, joined) });
else if (location.pathname.replace(/\/$/, "") === "/new") showAdmin(root);
else landing();
